/**
 * Hook del detalle de una Tarifa (US-2.4). La red vive en `tariffApi`; acá solo el
 * estado de la vista (loading / no encontrada / error / refresh), separable de la
 * presentación — mismo patrón que `useLocation`/`useAlly`.
 */
import { useCallback, useEffect, useState } from 'react';
import type { Tariff } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { tariffApi } from '../api/tariffApi';

export interface UseTariffResult {
  tariff: Tariff | null;
  loading: boolean;
  error: string | null;
  /** `true` cuando el backend responde 404 (tarifa inexistente). */
  notFound: boolean;
  refresh: () => void;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudo cargar la tarifa.';
}

export function useTariff(tariffId: string | undefined): UseTariffResult {
  const [tariff, setTariff] = useState<Tariff | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!tariffId) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);

    tariffApi
      .get(tariffId)
      .then((result) => {
        if (!cancelled) setTariff(result);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 404) {
          setNotFound(true);
        } else {
          setError(toErrorMessage(caught));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tariffId, reloadKey]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { tariff, loading, error, notFound, refresh };
}
